import { appAudioFocus, type AppAudioLease } from "@/lib/appAudioFocus";
import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setPlaybackMode, setRecordMode } from "@/lib/nativeAudioSession";
import { parsePaymentErrorFromResponse } from "@/lib/quotaError";
import { usePaymentErrorHandler } from "@/hooks/usePaymentErrorHandler";
import { mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { DEFAULT_LANG, normalizeLang, shortFor } from "@/i18n/locale";

const TTS_SUPABASE_URL =
  import.meta.env.NEXT_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const TTS_SUPABASE_ANON_KEY =
  import.meta.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export interface WhisperOwnerGuard { ownerId: string; authSessionId?: string; isCurrent: () => boolean }
export interface UseVoiceInputWhisperOptions {
  /** The bilingual tutor may omit the locale hint and detect each utterance. */
  detectLanguage?: boolean;
  /** Cost surface; absent keeps the existing Mirror attribution. */
  surface?: "mirror" | "work" | "teach_mirror";
}

export interface UseVoiceInputWhisperReturn {
  isListening: boolean;
  isTranscribing: boolean;
  transcript: string;
  recordingSeconds: number;
  amplitude: number;
  startListening: (ownerGuard?: WhisperOwnerGuard) => Promise<boolean>;
  stopListening: (ownerGuard?: WhisperOwnerGuard) => Promise<string>;
  cancelListening: (ownerGuard?: WhisperOwnerGuard) => void;
  supported: boolean;
}

interface Capture {
  focusLease: AppAudioLease | null;
  guard?: WhisperOwnerGuard;
  token: string | null;
  cancelled: boolean;
  chunks: Blob[];
  stream: MediaStream | null;
  recorder: MediaRecorder | null;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  raf: number | null;
  timer: number | null;
  upload: AbortController | null;
  stopPromise: Promise<string> | null;
  resolveStop: ((text: string) => void) | null;
  recordModeRequested: boolean;
}
const cancelledCapture = Symbol("cancelled_capture");
// A native mode call cannot be aborted. Share this queue across hook instances
// so an unmounted capture's restore finishes before a replacement opens its mic.
let audioModeQueue: Promise<unknown> = Promise.resolve();
function queueAudioMode(mode: () => Promise<unknown>) {
  const next = audioModeQueue.then(mode, mode);
  audioModeQueue = next.catch(() => {});
  return next;
}

export const useVoiceInputWhisper = (
  lang: string,
  options?: UseVoiceInputWhisperOptions,
): UseVoiceInputWhisperReturn => {
  const detectLanguage = options?.detectLanguage === true;
  const handlePaymentError = usePaymentErrorHandler();
  const handlePaymentErrorRef = useRef(handlePaymentError);
  useEffect(() => { handlePaymentErrorRef.current = handlePaymentError; }, [handlePaymentError]);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [amplitude, setAmplitude] = useState(0);
  const activeRef = useRef<Capture | null>(null);
  const mountedRef = useRef(true);
  const assertOwner = useCallback((guard?: WhisperOwnerGuard) => {
    if (guard && !guard.isCurrent()) throw new Error("invalid_session");
  }, []);
  const isCurrent = useCallback((capture: Capture) =>
    mountedRef.current && activeRef.current === capture && !capture.cancelled, []);
  const assertCurrent = useCallback((capture: Capture) => {
    if (!isCurrent(capture)) throw cancelledCapture;
    assertOwner(capture.guard);
  }, [isCurrent, assertOwner]);
  const ownedToken = useCallback(async (capture: Capture) => {
    const guard = capture.guard!;
    assertCurrent(capture);
    const user = await supabase.auth.getUser(); assertCurrent(capture);
    if (user.error || user.data.user?.id !== guard.ownerId) throw new Error("invalid_session");
    const session = await supabase.auth.getSession(); assertCurrent(capture);
    if (session.error || session.data.session?.user.id !== guard.ownerId || !session.data.session.access_token) throw new Error("invalid_session");
    if (guard.authSessionId !== undefined) {
      const identity = mirrorAuthIdentity(session.data.session);
      if (!identity || identity.ownerId !== guard.ownerId || identity.authSessionId !== guard.authSessionId) throw new Error("invalid_session");
    }
    return session.data.session.access_token;
  }, [assertCurrent]);

  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof MediaRecorder !== "undefined";

  const disposeRecording = useCallback((capture: Capture, stopRecorder = false) => {
    if (capture.raf !== null) { cancelAnimationFrame(capture.raf); capture.raf = null; }
    if (capture.timer !== null) { clearInterval(capture.timer); capture.timer = null; }
    const recorder = capture.recorder;
    capture.recorder = null;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      if (stopRecorder && recorder.state !== "inactive") {
        try { recorder.stop(); } catch { /* Tracks are still released below. */ }
      }
    }
    capture.stream?.getTracks().forEach(track => track.stop());
    capture.stream = null;
    if (capture.audioContext && capture.audioContext.state !== "closed") {
      void capture.audioContext.close().catch(() => {});
    }
    capture.audioContext = null;
    capture.analyser = null;
  }, []);
  const resetRecordingState = useCallback(() => {
    if (!mountedRef.current) return;
    setIsListening(false); setIsTranscribing(false);
    setAmplitude(0); setRecordingSeconds(0); setTranscript("");
  }, []);
  const cancelCapture = useCallback((capture: Capture) => {
    capture.cancelled = true;
    const focus = capture.focusLease; capture.focusLease = null;
    if (activeRef.current === capture) activeRef.current = null;
    capture.upload?.abort(); capture.upload = null;
    disposeRecording(capture, true);
    capture.chunks = [];
    capture.resolveStop?.(""); capture.resolveStop = null;
    if (capture.recordModeRequested) void queueAudioMode(setPlaybackMode).finally(() => focus?.release());
    else focus?.release();
  }, [disposeRecording]);
  const cancelListening = useCallback((ownerGuard?: WhisperOwnerGuard) => {
    const capture = activeRef.current;
    if (ownerGuard && capture?.guard && ownerGuard !== capture.guard) return;
    if (capture) cancelCapture(capture);
    resetRecordingState();
  }, [cancelCapture, resetRecordingState]);

  const startListening = useCallback(async (ownerGuard?: WhisperOwnerGuard) => {
    assertOwner(ownerGuard);
    if (!mountedRef.current) return false;
    if (!supported) { console.warn("[whisper] MediaRecorder not supported"); return false; }
    cancelListening();
    const capture: Capture = {
      focusLease: appAudioFocus.acquire("capture"), guard: ownerGuard, token: null, cancelled: false, chunks: [], stream: null,
      recorder: null, audioContext: null, analyser: null, raf: null, timer: null,
      upload: null, stopPromise: null, resolveStop: null, recordModeRequested: false,
    };
    activeRef.current = capture;
    try {
      capture.token = ownerGuard ? await ownedToken(capture) : null; assertCurrent(capture);
      await queueAudioMode(() => {
        if (!isCurrent(capture)) return Promise.resolve();
        capture.recordModeRequested = true;
        return setRecordMode();
      });
      assertCurrent(capture);
      // All voice processing stays off: iOS VoiceProcessingIO otherwise leaves
      // later TTS ducked. The turn-based Mirror does not need echo cancellation.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      if (!isCurrent(capture) || ownerGuard && !ownerGuard.isCurrent()) {
        stream.getTracks().forEach(track => track.stop());
        assertCurrent(capture);
      }
      capture.stream = stream;
      const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass();
      capture.audioContext = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; source.connect(analyser); capture.analyser = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateAmplitude = () => {
        if (!isCurrent(capture) || !capture.analyser) return;
        capture.analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        setAmplitude(Math.min(1, sum / dataArray.length / 128));
        capture.raf = requestAnimationFrame(updateAmplitude);
      };
      capture.raf = requestAnimationFrame(updateAmplitude);
      let mimeType = "audio/webm";
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "audio/mp4";
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "";
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      capture.recorder = recorder;
      recorder.ondataavailable = event => {
        if (isCurrent(capture) && (!capture.guard || capture.guard.isCurrent()) && event.data.size > 0) capture.chunks.push(event.data);
      };
      recorder.start(250);
      const startedAt = Date.now();
      capture.timer = window.setInterval(() => {
        if (isCurrent(capture)) setRecordingSeconds(Math.floor((Date.now() - startedAt) / 1000));
      }, 500);
      setIsListening(true);
      return true;
    } catch (err) {
      if (!isCurrent(capture)) return false;
      cancelCapture(capture); resetRecordingState();
      if (err instanceof Error && err.message === "native_audio_session_busy") {
        console.warn("[whisper] recording unavailable during active LIVE", err);
      } else {
        console.error("[whisper] startListening failed", err);
      }
      if (ownerGuard) throw err;
      return false;
    }
  }, [assertOwner, supported, cancelListening, ownedToken, assertCurrent, isCurrent, cancelCapture, resetRecordingState]);

  const stopListening = useCallback(async (ownerGuard?: WhisperOwnerGuard): Promise<string> => {
    const capture = activeRef.current;
    if (ownerGuard && capture?.guard && ownerGuard !== capture.guard) return "";
    if (!capture) { setIsListening(false); return ""; }
    if (ownerGuard && !ownerGuard.isCurrent() || capture.guard && !capture.guard.isCurrent()) {
      cancelListening(ownerGuard); return "";
    }
    if (capture.stopPromise) return capture.stopPromise;
    const recorder = capture.recorder;
    if (!recorder || recorder.state === "inactive") { cancelListening(ownerGuard); return ""; }
    capture.stopPromise = new Promise<string>(resolve => { capture.resolveStop = resolve; });
    const finish = (text: string, publish = false) => {
      if (activeRef.current === capture) {
        activeRef.current = null;
        if (mountedRef.current) {
          setIsListening(false); setIsTranscribing(false); setAmplitude(0); setRecordingSeconds(0);
          if (publish) setTranscript(text);
        }
      }
      capture.chunks = []; capture.upload = null;
      capture.resolveStop?.(text); capture.resolveStop = null;
    };
    recorder.onstop = async () => {
      if (!isCurrent(capture)) { finish(""); return; }
      const blob = new Blob(capture.chunks, { type: recorder.mimeType || "audio/webm" });
      capture.chunks = [];
      disposeRecording(capture);
      setIsListening(false); setIsTranscribing(true); setAmplitude(0); setRecordingSeconds(0);
      void queueAudioMode(setPlaybackMode).finally(() => { capture.focusLease?.release(); capture.focusLease = null; });
      const controller = new AbortController(); capture.upload = controller;
      try {
        assertCurrent(capture);
        const whisperLang = shortFor(normalizeLang(lang) ?? DEFAULT_LANG);
        const form = new FormData(); form.append("audio", blob, "audio.webm"); form.append("language", whisperLang);
        if (detectLanguage) form.append("language_mode", "auto");
        if (options?.surface) form.append("surface", options.surface);
        const token = capture.guard ? await ownedToken(capture) : (await supabase.auth.getSession()).data.session?.access_token ?? "";
        assertCurrent(capture);
        const response = await fetch(`${TTS_SUPABASE_URL}/functions/v1/mirror-stt`, {
          method: "POST", headers: { Authorization: "Bearer " + (capture.token ?? token), apikey: TTS_SUPABASE_ANON_KEY },
          body: form, signal: controller.signal,
        });
        assertCurrent(capture);
        if (!response.ok) {
          const paymentError = await parsePaymentErrorFromResponse(response); assertCurrent(capture);
          if (paymentError) handlePaymentErrorRef.current?.(paymentError);
          else console.error("[whisper] transcription failed", response.status);
          finish(""); return;
        }
        const result = await response.json(); assertCurrent(capture);
        const text = (result.transcript ?? "").trim();
        finish(text, true);
      } catch (err) {
        if (isCurrent(capture) && (!capture.guard || capture.guard.isCurrent())) console.error("[whisper] upload failed", err);
        finish("");
      }
    };
    try { recorder.stop(); } catch (err) {
      disposeRecording(capture, true); void queueAudioMode(setPlaybackMode).finally(() => { capture.focusLease?.release(); capture.focusLease = null; });
      console.error("[whisper] stopListening failed", err); finish("");
    }
    return capture.stopPromise;
  }, [cancelListening, isCurrent, disposeRecording, assertCurrent, lang, detectLanguage, options?.surface, ownedToken]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const capture = activeRef.current;
      if (capture) cancelCapture(capture);
    };
  }, [cancelCapture]);

  return { isListening, isTranscribing, transcript, recordingSeconds, amplitude,
    startListening, stopListening, cancelListening, supported };
};
